import { fetchTaskIdforStatusUpdate } from 'app/components/Tasks/TaskDetails/helper';
import type { ITaskChainItem, ITaskChainRecipient, IUpdateTaskChainStatusRequest } from 'app/models/Tasks/TaskChain';
import { TaskStatus } from 'app/models/Tasks/Tasks';
import type { ICoverageDetails } from 'app/models/UserCoverage/UserCoverage';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';

/** Minimal assignee shape expected by fetchTaskIdforStatusUpdate (matches TaskDetails / Cosmos task rows). */
export type LegacyRecipientRow = {
  id: number;
  statusId: number;
  firstName?: string;
  lastName?: string;
};

export type LegacyTaskDataRow = {
  taskId: number;
  rootTaskId: number;
  assignedTo: LegacyRecipientRow[];
  cc: LegacyRecipientRow[];
};

export type LegacyCommentRow = {
  taskId: number;
  tos: LegacyRecipientRow[];
  ccs: LegacyRecipientRow[];
};

export function recipientToLegacyRow(r: ITaskChainRecipient): LegacyRecipientRow {
  return {
    id: r.recipientId,
    statusId: r.statusId,
    firstName: r.firstName,
    lastName: r.lastName,
  };
}

export function chainRecipientsToAssignedToAndCc(recipients: ITaskChainRecipient[] | undefined | null): {
  assignedTo: LegacyRecipientRow[];
  cc: LegacyRecipientRow[];
} {
  const assignedTo: LegacyRecipientRow[] = [];
  const cc: LegacyRecipientRow[] = [];
  for (const r of recipients || []) {
    const row = recipientToLegacyRow(r);
    if (r.isCC) cc.push(row);
    else assignedTo.push(row);
  }
  return { assignedTo, cc };
}

/**
 * Split flattened chain into root row + "comment" rows (subtasks), matching TaskDetails + selectTaskComments layout.
 */
export function splitChainRootAndComments(flatTasks: ITaskChainItem[]): {
  taskData: LegacyTaskDataRow | null;
  commentList: LegacyCommentRow[];
} {
  if (!flatTasks.length) {
    return { taskData: null, commentList: [] };
  }
  const root = flatTasks.find(t => t.taskId === t.rootTaskId) ?? flatTasks[0];
  const { assignedTo, cc } = chainRecipientsToAssignedToAndCc(root.recipients);
  const taskData: LegacyTaskDataRow = {
    taskId: root.taskId,
    rootTaskId: root.rootTaskId,
    assignedTo,
    cc,
  };
  const commentList: LegacyCommentRow[] = flatTasks
    .filter(t => t.taskId !== root.taskId)
    .map(t => {
      const split = chainRecipientsToAssignedToAndCc(t.recipients);
      return { taskId: t.taskId, tos: split.assignedTo, ccs: split.cc };
    });
  return { taskData, commentList };
}

export function getActingEmployeeIdForReadStatus(userInfo: {
  userInfo?: {
    employeeId?: number;
    sharedProfile?: { employeeId?: string | number };
  };
} | null): number | undefined {
  if (!userInfo?.userInfo) return undefined;
  if (!isNullOrUndefined(userInfo.userInfo.sharedProfile)) {
    const id = userInfo.userInfo.sharedProfile?.employeeId;
    return id != null ? parseInt(String(id), 10) : undefined;
  }
  return userInfo.userInfo.employeeId;
}

function selectedTaskIdsToStatusRequests(params: {
  taskData: LegacyTaskDataRow;
  commentList: LegacyCommentRow[];
  recipientId: number;
  isOnBehalfOf: boolean;
}): IUpdateTaskChainStatusRequest[] {
  const { taskData, commentList, recipientId, isOnBehalfOf } = params;
  const { selectedTaskIdsforUpdate } = fetchTaskIdforStatusUpdate(taskData, commentList, recipientId);
  if (!selectedTaskIdsforUpdate?.length) return [];
  return selectedTaskIdsforUpdate.map(taskId => ({
    rootTaskId: taskData.rootTaskId,
    taskId,
    applyToWholeChain: false,
    recipientId,
    statusId: TaskStatus.Pending,
    isOnBehalfOf,
    // New→Pending: request SP cascade (same as modal when not "Only My Task") so coverer/covered rows stay aligned.
    updateCoveredEmployee: true,
  }));
}

function collectCoverageRecipientsWithNew(
  flatTasks: ITaskChainItem[],
  activeCoverage: ICoverageDetails[] | null | undefined,
): LegacyRecipientRow[] {
  const coverage = activeCoverage || [];
  const byId = new Map<number, LegacyRecipientRow>();
  for (const task of flatTasks) {
    const { assignedTo, cc } = chainRecipientsToAssignedToAndCc(task.recipients);
    for (const r of [...assignedTo, ...cc]) {
      if (r.statusId !== TaskStatus.New) continue;
      if (!coverage.some(c => c.coveredEmployeeId === r.id)) continue;
      if (!byId.has(r.id)) byId.set(r.id, r);
    }
  }
  return Array.from(byId.values());
}

/**
 * Builds PUT `/tasks/status` bodies for notification-open read (New → Pending), self + coverage,
 * from a flattened task chain (same selection as legacy POST via fetchTaskIdforStatusUpdate).
 */
export function buildNotificationOpenReadStatusRequests(
  flatTasks: ITaskChainItem[],
  userInfo: Parameters<typeof getActingEmployeeIdForReadStatus>[0],
  activeCoverage: ICoverageDetails[] | null | undefined,
): IUpdateTaskChainStatusRequest[] {
  const { taskData, commentList } = splitChainRootAndComments(flatTasks);
  if (!taskData?.rootTaskId) return [];

  const requests: IUpdateTaskChainStatusRequest[] = [];
  const actingId = getActingEmployeeIdForReadStatus(userInfo);
  if (actingId != null && actingId > 0) {
    requests.push(
      ...selectedTaskIdsToStatusRequests({
        taskData,
        commentList,
        recipientId: actingId,
        isOnBehalfOf: false,
      }),
    );
  }

  const coveringEmployeeId = userInfo?.userInfo?.employeeId;
  if (coveringEmployeeId != null && coveringEmployeeId > 0) {
    const covered = collectCoverageRecipientsWithNew(flatTasks, activeCoverage);
    for (const recipient of covered) {
      requests.push(
        ...selectedTaskIdsToStatusRequests({
          taskData,
          commentList,
          recipientId: recipient.id,
          isOnBehalfOf: true,
        }),
      );
    }
  }

  return requests;
}
